import React, { useState, useRef } from 'react';
import { Box, Button, Container, TextField, Typography, Paper, Grid, CircularProgress, useTheme, MenuItem, Select, FormControl, InputLabel, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import DownloadIcon from '@mui/icons-material/Download';
// @ts-ignore
import html2canvas from 'html2canvas';

interface Match {
  teamA: string;
  teamB: string;
}

// Komponen visualisasi bracket tree dengan garis SVG dan warna responsif theme
const BracketTree: React.FC<{ bracket: Match[][]; names: string[] }> = ({ bracket, names }) => {
  const theme = useTheme();
  if (bracket.length === 0) return null;
  const nodeHeight = 40;
  const nodeGap = 32;
  const colWidth = 140;
  const rounds = bracket.length;
  const totalNodes = bracket[0].length * 2;
  // Deteksi jumlah bye
  const byeIdx = names.map((n, i) => (!n || n.trim() === '' || n.trim() === '-') ? i : -1).filter(i => i !== -1);
  let displayNames = [...names];
  if (byeIdx.length === 2) {
    // Jika ada 2 bye, pastikan index 0 dan terakhir yang kosong diisi 'bye', sisanya tetap urut
    if (byeIdx.includes(0)) displayNames[0] = 'bye';
    if (byeIdx.includes(displayNames.length - 1)) displayNames[displayNames.length - 1] = 'bye';
    for (let i = 1; i < displayNames.length - 1; i++) {
      if (byeIdx.includes(i)) displayNames[i] = names[i] || 'bye';
    }
  } else {
    // Jika 1 atau lebih dari 2 bye, semua slot kosong diisi 'bye'
    displayNames = displayNames.map((n) => (!n || n.trim() === '' || n.trim() === '-') ? 'bye' : n);
  }
  // Perbaiki: hitung height berdasarkan node babak pertama, lalu FINAL selalu center
  const height = Math.max(totalNodes * nodeHeight + (totalNodes - 1) * nodeGap, nodeHeight * 2 + nodeGap);
  const width = (rounds + 1) * colWidth + 10;
  const bgNode = theme.palette.mode === 'dark' ? '#222' : '#fff';
  const borderNode = theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main;
  const textNode = theme.palette.mode === 'dark' ? '#fff' : '#222';
  const lineColor = theme.palette.primary.main;


  // Render node dan garis antar babak
  const renderBracket = () => {
    const nodes: any[] = [];
    const lines: any[] = [];
    // Babak pertama (peserta)
    for (let i = 0; i < totalNodes; i++) {
      nodes.push(
        <Box key={`node-0-${i}`}
          sx={{
            position: 'absolute',
            left: 0,
            top: i * (nodeHeight + nodeGap),
            width: colWidth,
            height: nodeHeight,
            border: `2px solid ${borderNode}`,
            borderRadius: 2,
            background: bgNode,
            color: textNode,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            fontWeight: 600,
            fontSize: 15,
            zIndex: 2,
            boxShadow: 1
          }}>
          {displayNames[i]}
        </Box>
      );
    }
    // Babak berikutnya
    let prevCount = totalNodes;
    let prevY: number[] = [];
    for (let i = 0; i < totalNodes; i++) prevY.push(i * (nodeHeight + nodeGap) + nodeHeight / 2);
    let finalY = height / 2;
    for (let r = 1; r <= rounds; r++) {
      const count = prevCount / 2;
      const yArr: number[] = [];
      for (let i = 0; i < count; i++) {
        // Perbaiki: selalu ambil dua node di bawahnya, walaupun salah satunya bye
        const idx1 = i * 2;
        const idx2 = i * 2 + 1;
        const y1 = prevY[idx1];
        const y2 = prevY[idx2];
        // Node pemenang selalu di tengah dua node di bawahnya
        const y = (y1 + y2) / 2;
        let top = y - nodeHeight / 2;
        if (r === rounds) {
          top = height / 2 - nodeHeight / 2;
        }
        nodes.push(
          <Box key={`node-${r}-${i}`}
            sx={{
              position: 'absolute',
              left: r * colWidth,
              top,
              width: colWidth,
              height: nodeHeight,
              border: `2px solid ${r === rounds ? theme.palette.error.main : borderNode}`,
              borderRadius: 2,
              background: r === rounds ? (theme.palette.mode === 'dark' ? '#fff' : '#fff') : bgNode,
              color: r === rounds ? theme.palette.error.main : textNode,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: r === rounds ? 700 : 600,
              fontSize: r === rounds ? 18 : 15,
              zIndex: 2,
              boxShadow: 1
            }}>
            {r === rounds ? 'Winner' : ''}
          </Box>
        );
        // Garis dari node sebelumnya ke node ini
        if (r === rounds) {
          for (let j = 0; j < prevY.length; j++) {
            lines.push(
              <line key={`line-final-${j}`} x1={(r - 1) * colWidth + colWidth} y1={prevY[j]} x2={r * colWidth} y2={finalY} stroke={lineColor} strokeWidth={3} style={{stroke: lineColor}} />
            );
          }
          break;
        } else {
          lines.push(
            <line key={`line1-${r}-${i}`} x1={(r - 1) * colWidth + colWidth} y1={y1} x2={r * colWidth} y2={y} stroke={lineColor} strokeWidth={3} style={{stroke: lineColor}} />
          );
          lines.push(
            <line key={`line2-${r}-${i}`} x1={(r - 1) * colWidth + colWidth} y1={y2} x2={r * colWidth} y2={y} stroke={lineColor} strokeWidth={3} style={{stroke: lineColor}} />
          );
          lines.push(
            <line key={`line3-${r}-${i}`} x1={r * colWidth} y1={y} x2={r * colWidth + colWidth / 2} y2={y} stroke={lineColor} strokeWidth={3} style={{stroke: lineColor}} />
          );
        }
        yArr.push(y);
      }
      prevCount = count;
      prevY = yArr;
    }
    return (
      <Box sx={{ position: 'relative', width, height, mx: 'auto', my: 2 }}>
        <svg width={width} height={height} style={{ position: 'absolute', left: 0, top: 0, zIndex: 1, pointerEvents: 'none' }}>
          {lines}
        </svg>
        {nodes}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 4, mb: 2, overflowX: 'auto', bgcolor: theme.palette.background.default, borderRadius: 2, p: 2 }}>
      {renderBracket()}
    </Box>
  );
};

const BracketGenerator: React.FC = () => {
  const theme = useTheme();
  const [mode, setMode] = useState<'tim' | 'ganda'>('tim');
  const [teamCount, setTeamCount] = useState(8);
  const [names, setNames] = useState<string[]>(Array.from({ length: 8 }, (_, i) => `Tim ${i + 1}`));
  const [systemType, setSystemType] = useState<'gugur' | 'grup'>('gugur');
  // Untuk mode ganda
  const [level1, setLevel1] = useState('');
  const [level2, setLevel2] = useState('');
  const [level3, setLevel3] = useState('');
  const [pairs, setPairs] = useState<string[][]>([]);
  const [bracket, setBracket] = useState<any[][]>([]);
  const [groups, setGroups] = useState<string[][]>([]);
  const [groupLabels, setGroupLabels] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isShuffling, setIsShuffling] = useState(false);
  const [revealedNames, setRevealedNames] = useState(0);
  const [shuffledNames, setShuffledNames] = useState<string[]>([]);
  const bracketRef = useRef<HTMLDivElement>(null);

  // Update names array jika jumlah tim berubah
  const handleTeamCountChange = (event: any) => {
    const value = parseInt(event.target.value, 10);
    setTeamCount(value);
    setNames(Array.from({ length: value }, (_, i) => `Tim ${i + 1}`));
    setBracket([]);
    setGroups([]);
    setRevealedNames(0);
  };

  const handleNameChange = (idx: number, value: string) => {
    const newNames = [...names];
    newNames[idx] = value;
    setNames(newNames);
  };

  const handleSystemTypeChange = (event: any) => {
    const value = event.target.value as 'gugur' | 'grup';
    setSystemType(value);
    setError('');
    setBracket([]);
    setGroups([]);
    setShuffledNames([]);
    setRevealedNames(0);
  };

  const generateBracket = (finalNames: string[]) => {
    const rounds: Match[][] = [];
    let current = finalNames;
    let roundNum = 0;
    while (current.length > 1) {
      const round: Match[] = [];
      for (let i = 0; i < current.length; i += 2) {
        round.push({ teamA: current[i], teamB: current[i + 1] });
      }
      rounds.push(round);
      // Untuk round 2 dst, node kosong
      if (roundNum === 0) {
        current = round.map(() => '');
      } else {
        current = round.map(() => '');
      }
      roundNum++;
    }
    setBracket(rounds);
  };

  // Fungsi acak partner ganda
  function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  const handleShuffle = async () => {
    setError('');
    setBracket([]);
    setGroups([]);
    setRevealedNames(0);
    // Jika nama kosong, isi dengan default
    const filledNames = names.map((n, i) => n.trim() || `Tim ${i + 1}`);
    if (filledNames.length !== teamCount) {
      setError('Semua nama tim/pemain harus diisi.');
      return;
    }
    setIsShuffling(true);
    // Simulasi animasi pengacakan (acak beberapa kali sebelum hasil akhir)
    let tempNames = filledNames;
    for (let i = 0; i < 18; i++) { // lebih lama, lebih mendebarkan
      tempNames = shuffleArray(tempNames);
      setShuffledNames([...tempNames]);
      await new Promise(res => setTimeout(res, 180)); // delay lebih lama
    }
    // Hasil akhir
    const finalNames = shuffleArray(filledNames);
    setShuffledNames(finalNames);
    // Animasi reveal nama satu per satu
    for (let i = 1; i <= finalNames.length; i++) {
      setRevealedNames(i);
      await new Promise(res => setTimeout(res, 3000)); // delay lebih lama
    }
    setIsShuffling(false);
    if (systemType === 'gugur') {
      generateBracket(finalNames);
    } else {
      // Sistem grup: bagi pemain ke beberapa grup secara acak dan merata
      // Tentukan jumlah grup berdasarkan jumlah peserta
      let groupCount = 2;
      if (finalNames.length > 8 && finalNames.length <= 16) {
        groupCount = 4;
      } else if (finalNames.length > 16) {
        groupCount = 8;
      }
      if (groupCount > finalNames.length) {
        groupCount = finalNames.length;
      }
      const groupArr: string[][] = Array.from({ length: groupCount }, () => []);
      finalNames.forEach((name, idx) => {
        const gIdx = idx % groupCount;
        groupArr[gIdx].push(name);
      });
      const labels = Array.from({ length: groupCount }, (_, i) => `Grup ${String.fromCharCode(65 + i)}`);
      setGroups(groupArr);
      setGroupLabels(labels);
    }
  };

  const handleShuffleGanda = async () => {
    setError('');
    setBracket([]);
    setPairs([]);
    setShuffledNames([]);
    setGroups([]);
    setRevealedNames(0);
    setIsShuffling(true);
    // Ambil nama per level
    const l1 = level1.split('\n').map(s => s.trim()).filter(Boolean);
    const l2 = level2.split('\n').map(s => s.trim()).filter(Boolean);
    const l3 = level3.split('\n').map(s => s.trim()).filter(Boolean);
    // Validasi jumlah
    if (l1.length !== l3.length) {
      setError('Jumlah Level 1 dan Level 3 harus sama!');
      setIsShuffling(false);
      return;
    }
    if (l2.length % 2 !== 0) {
      setError('Jumlah Level 2 harus genap!');
      setIsShuffling(false);
      return;
    }
    // Acak partner
    const l1s = shuffleArray(l1);
    const l3s = shuffleArray(l3);
    const l2s = shuffleArray(l2);
    const pasangan: string[][] = [];
    for (let i = 0; i < l1s.length; i++) {
      pasangan.push([l1s[i], l3s[i]]);
    }
    for (let i = 0; i < l2s.length; i += 2) {
      pasangan.push([l2s[i], l2s[i + 1]]);
    }
    setPairs(pasangan);
    // Animasi reveal pasangan
    for (let i = 1; i <= pasangan.length; i++) {
      setRevealedNames(i);
      await new Promise(res => setTimeout(res, 3000)); // delay lebih lama
    }
    setIsShuffling(false);
    // Acak urutan pasangan untuk bracket
    const pasanganStr = pasangan.map(p => p.join(' & '));
    // --- Tambahkan sistem bye dengan distribusi agar tidak ada bye bertemu bye ---
    let peserta = [...pasanganStr];
    const nextPow2 = Math.pow(2, Math.ceil(Math.log2(peserta.length)));
    const byeCount = nextPow2 - peserta.length;
    let slotArr = Array(nextPow2).fill(null);
    // Tempatkan bye di slot berjauhan (misal: 0, 2, 4, dst)
    let byePlaced = 0;
    for (let i = 0; i < nextPow2 && byePlaced < byeCount; i += 2) {
      slotArr[i] = 'bye';
      byePlaced++;
    }
    // Isi peserta ke slot kosong
    let pesertaIdx = 0;
    for (let i = 0; i < nextPow2; i++) {
      if (!slotArr[i]) {
        slotArr[i] = peserta[pesertaIdx++];
      }
    }
    setShuffledNames(slotArr);
    generateBracket(slotArr);
  };

  // Perbaiki handleSaveImage agar tidak terpotong
  const handleSaveImage = async () => {
    if (bracketRef.current) {
      // Pastikan bracket tidak terpotong
      bracketRef.current.style.overflow = 'visible';
      bracketRef.current.style.width = 'fit-content';
      bracketRef.current.style.minWidth = '100%';
      bracketRef.current.style.minHeight = '100%';
      await new Promise(res => setTimeout(res, 200));
      html2canvas(bracketRef.current, {
        useCORS: true,
        backgroundColor: theme.palette.background.paper,
        scale: 2
      }).then(canvas => {
        const link = document.createElement('a');
        link.download = 'bracket.png';
        link.href = canvas.toDataURL();
        link.click();
      });
    }
  };

  const handleModeChange = (e: any) => {
    setMode(e.target.value);
    setError('');
    setBracket([]);
    setPairs([]);
    setGroups([]);
    setShuffledNames([]);
    setRevealedNames(0);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Bracket Generator
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
        <FormControl>
          <RadioGroup row value={mode} onChange={handleModeChange}>
            <FormControlLabel value="tim" control={<Radio />} label="Pengocokan Tim" />
            <FormControlLabel value="ganda" control={<Radio />} label="Pengocokan Pemain (Ganda)" />
          </RadioGroup>
        </FormControl>
      </Box>
      {mode === 'tim' && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
            <FormControl>
              <InputLabel id="team-count-label">Jumlah Tim</InputLabel>
              <Select
                labelId="team-count-label"
                value={teamCount}
                label="Jumlah Tim"
                onChange={handleTeamCountChange}
                sx={{ minWidth: 120 }}
              >
                {[2, 4, 8, 16, 32, 64].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel id="system-type-label">Tipe Sistem</InputLabel>
              <Select
                labelId="system-type-label"
                value={systemType}
                label="Tipe Sistem"
                onChange={handleSystemTypeChange}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="gugur">Sistem Gugur</MenuItem>
                <MenuItem value="grup">Sistem Grup</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ShuffleIcon />}
              onClick={handleShuffle}
              disabled={isShuffling}
              sx={{ mr: 2 }}
            >
              {isShuffling ? 'Mengacak...' : systemType === 'gugur' ? 'Acak Bagan' : 'Acak Grup'}
            </Button>
            {!isShuffling && ((systemType === 'gugur' && bracket.length > 0) || (systemType === 'grup' && groups.length > 0)) && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleSaveImage}
              >
                Simpan sebagai Gambar
              </Button>
            )}
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {names.map((name, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <TextField
                  label={`Nama Tim/Pemain ${idx + 1}`}
                  value={name}
                  onChange={e => handleNameChange(idx, e.target.value)}
                  fullWidth
                  disabled={isShuffling}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {mode === 'ganda' && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<ShuffleIcon />}
              onClick={handleShuffleGanda}
              disabled={isShuffling}
              sx={{ mr: 2 }}
            >
              {isShuffling ? 'Mengacak...' : 'Acak Partner & Bagan'}
            </Button>
            {!isShuffling && bracket.length > 0 && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleSaveImage}
              >
                Simpan sebagai Gambar
              </Button>
            )}
          </Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Level 1 (1 nama per baris)"
                value={level1}
                onChange={e => setLevel1(e.target.value)}
                fullWidth
                multiline
                minRows={6}
                disabled={isShuffling}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Level 2 (1 nama per baris)"
                value={level2}
                onChange={e => setLevel2(e.target.value)}
                fullWidth
                multiline
                minRows={6}
                disabled={isShuffling}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Level 3 (1 nama per baris)"
                value={level3}
                onChange={e => setLevel3(e.target.value)}
                fullWidth
                multiline
                minRows={6}
                disabled={isShuffling}
              />
            </Grid>
          </Grid>
          {/* Tampilkan hasil pasangan ganda */}
          {!isShuffling && pairs.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600} align="center" sx={{ mb: 1 }}>
                Hasil Pasangan Ganda
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                {pairs.map((p, idx) => (
                  <Grid item key={idx} xs={12} sm={6} md={4} lg={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', fontWeight: 700, fontSize: 16, border: '2px solid', borderColor: theme.palette.primary.main }}>
                      {p[0]} &amp; {p[1]}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
      {error && <Typography color="error" align="center" sx={{ mb: 2 }}>{error}</Typography>}
      {/* Animasi reveal nama/pasangan */}
      {isShuffling && mode === 'tim' && (
        <Box textAlign="center" mb={2}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography fontWeight={600} color="primary">Sedang mengacak...</Typography>
          <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
            {shuffledNames.slice(0, revealedNames).map((n, i) => (
              <Typography
                key={i}
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'opacity 0.6s, transform 0.6s',
                  animation: 'fadeInUp 0.7s',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(30px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                {n}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {isShuffling && mode === 'ganda' && (
        <Box textAlign="center" mb={2}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography fontWeight={600} color="primary">Sedang mengacak...</Typography>
          <Box mt={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
            {pairs.slice(0, revealedNames).map((p, i) => (
              <Typography
                key={i}
                variant="h6"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 1,
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'opacity 0.6s, transform 0.6s',
                  animation: 'fadeInUp 0.7s',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(30px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                {p[0]} & {p[1]}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {/* Setelah animasi selesai, tampilkan bracket */}
      <Box
        ref={bracketRef}
        sx={{
          overflow: 'visible',
          width: 'fit-content',
          minWidth: '100%',
          minHeight: '100%',
          background: theme.palette.background.paper,
          p: 2,
          m: 'auto',
        }}
      >
        {!isShuffling && systemType === 'gugur' && bracket.length > 0 && (
          <Box>
            <Typography variant="h6" fontWeight={600} align="center" sx={{ mb: 2 }}>
              Hasil Bagan Sistem Gugur
            </Typography>
            <BracketTree bracket={bracket} names={shuffledNames.length ? shuffledNames : names} />
          </Box>
        )}
        {!isShuffling && systemType === 'grup' && groups.length > 0 && (
          <Box>
            <Typography variant="h6" fontWeight={600} align="center" sx={{ mb: 2 }}>
              Hasil Drawing Sistem Grup
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {groups.map((group, idx) => (
                <Grid item key={idx} xs={12} sm={6} md={4} lg={3}>
                  <Paper
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: theme.palette.primary.main,
                      minHeight: 120,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} align="center" sx={{ mb: 1 }}>
                      {groupLabels[idx] || `Grup ${idx + 1}`}
                    </Typography>
                    {group.map((member, i) => (
                      <Typography key={i} variant="body1" align="center">
                        {member}
                      </Typography>
                    ))}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
      
    </Container>
  );
};

export default BracketGenerator; 